/**
 * Integrations Domain Seed Data
 *
 * Contains all seed data for Integration Hub Service (3):
 * - Connectors (external service connections)
 * - Connector Credentials (encrypted)
 * - Webhook Subscriptions
 * - Webhook Deliveries
 * - Enrichment Jobs
 * - Prospect Import Jobs
 * - Platform Integration Configs
 * - Business Integration Configs
 * - Enrichment Portal Jobs (business portal enrichment UI)
 *
 * @see PR #262 - SFR Analytics Integration
 */

import { seedId } from '../utils';
import { BUSINESS_IDS } from './tenants';
import { USER_IDS } from './users';

// =============================================================================
// CONNECTOR IDs
// =============================================================================

export const CONNECTOR_IDS = {
  // OpsMatrix Capital connectors
  FUNDING_SFR_ANALYTICS: seedId('connector-funding-sfr', 1),
  FUNDING_SALESFORCE: seedId('connector-funding-sf', 1),
  FUNDING_SLACK: seedId('connector-funding-slack', 1),
  FUNDING_SMARTY: seedId('connector-funding-smarty', 1),

  // 1Sharpe Capital connectors
  SHARPE_SFR_ANALYTICS: seedId('connector-sharpe-sfr', 1),
  SHARPE_HUBSPOT: seedId('connector-sharpe-hubspot', 1),

  // Pacific Bridge Fund connectors
  PACIFIC_SLACK: seedId('connector-pacific-slack', 1),
} as const;

export type ConnectorId = (typeof CONNECTOR_IDS)[keyof typeof CONNECTOR_IDS];

// =============================================================================
// WEBHOOK SUBSCRIPTION IDs
// =============================================================================

export const WEBHOOK_SUBSCRIPTION_IDS = {
  FUNDING_DEAL_UPDATES: seedId('webhook-funding-deals', 1),
  FUNDING_PAYMENT_EVENTS: seedId('webhook-funding-payments', 1),
  SHARPE_LEAD_EVENTS: seedId('webhook-sharpe-leads', 1),
} as const;

export type WebhookSubscriptionId = (typeof WEBHOOK_SUBSCRIPTION_IDS)[keyof typeof WEBHOOK_SUBSCRIPTION_IDS];

// =============================================================================
// ENRICHMENT JOB IDs
// =============================================================================

export const ENRICHMENT_JOB_IDS = {
  FUNDING_LEAD_BATCH_1: seedId('enrich-job-funding', 1),
  FUNDING_PROPERTY_BATCH_1: seedId('enrich-job-funding-prop', 1),
  SHARPE_LEAD_BATCH_1: seedId('enrich-job-sharpe', 1),
} as const;

export type EnrichmentJobId = (typeof ENRICHMENT_JOB_IDS)[keyof typeof ENRICHMENT_JOB_IDS];

// =============================================================================
// PROSPECT IMPORT JOB IDs
// =============================================================================

export const PROSPECT_IMPORT_JOB_IDS = {
  FUNDING_AUSTIN_IMPORT: seedId('import-job-funding-austin', 1),
  FUNDING_DALLAS_IMPORT: seedId('import-job-funding-dallas', 1),
} as const;

export type ProspectImportJobId = (typeof PROSPECT_IMPORT_JOB_IDS)[keyof typeof PROSPECT_IMPORT_JOB_IDS];

// =============================================================================
// PLATFORM INTEGRATION CONFIG IDs
// =============================================================================

export const PLATFORM_INTEGRATION_CONFIG_IDS = {
  RESEND_PLATFORM: seedId('platform-config-resend', 1),
  RETELL_PLATFORM: seedId('platform-config-retell', 1),
  OPENAI_PLATFORM: seedId('platform-config-openai', 1),
  ANTHROPIC_PLATFORM: seedId('platform-config-anthropic', 1),
  SFR_ANALYTICS_PLATFORM: seedId('platform-config-sfr', 1),
  DOCUSEAL_PLATFORM: seedId('platform-config-docuseal', 1), // Issue #300: E-Sign service
} as const;

export type PlatformIntegrationConfigId =
  (typeof PLATFORM_INTEGRATION_CONFIG_IDS)[keyof typeof PLATFORM_INTEGRATION_CONFIG_IDS];

// =============================================================================
// WEBHOOK DELIVERY IDs
// =============================================================================

export const WEBHOOK_DELIVERY_IDS = {
  // OpsMatrix Capital deliveries
  FUNDING_DEAL_DELIVERY_1: seedId('webhook-delivery-funding-deal-1', 1),
  FUNDING_DEAL_DELIVERY_2: seedId('webhook-delivery-funding-deal-2', 2),
  FUNDING_PAYMENT_DELIVERY_1: seedId('webhook-delivery-funding-payment-1', 1),
  FUNDING_PAYMENT_DELIVERY_FAILED: seedId('webhook-delivery-funding-payment-fail', 1),

  // 1Sharpe Capital deliveries
  SHARPE_LEAD_DELIVERY_1: seedId('webhook-delivery-sharpe-lead-1', 1),
} as const;

export type WebhookDeliveryId = (typeof WEBHOOK_DELIVERY_IDS)[keyof typeof WEBHOOK_DELIVERY_IDS];

// =============================================================================
// CONNECTOR USAGE IDs
// =============================================================================

export const CONNECTOR_USAGE_IDS = {
  // OpsMatrix Capital usage
  FUNDING_SFR_SEARCH_JAN21: seedId('usage-funding-sfr-search-jan21', 1),
  FUNDING_SFR_ENRICH_JAN21: seedId('usage-funding-sfr-enrich-jan21', 1),
  FUNDING_SALESFORCE_SYNC_JAN21: seedId('usage-funding-sf-sync-jan21', 1),
  FUNDING_SMARTY_VALIDATE_JAN21: seedId('usage-funding-smarty-jan21', 1),

  // 1Sharpe Capital usage
  SHARPE_SFR_SEARCH_JAN21: seedId('usage-sharpe-sfr-search-jan21', 1),
  SHARPE_HUBSPOT_SYNC_JAN21: seedId('usage-sharpe-hubspot-jan21', 1),
} as const;

export type ConnectorUsageId = (typeof CONNECTOR_USAGE_IDS)[keyof typeof CONNECTOR_USAGE_IDS];

// =============================================================================
// BATCH ENRICHMENT JOB IDs
// =============================================================================

export const BATCH_ENRICHMENT_JOB_IDS = {
  // OpsMatrix Capital batch jobs
  FUNDING_LEAD_BATCH_WEEKLY: seedId('batch-enrich-funding-weekly', 1),
  FUNDING_PROPERTY_BATCH_DAILY: seedId('batch-enrich-funding-prop-daily', 1),

  // 1Sharpe Capital batch jobs
  SHARPE_CONSTRUCTION_BATCH: seedId('batch-enrich-sharpe-const', 1),
} as const;

export type BatchEnrichmentJobId = (typeof BATCH_ENRICHMENT_JOB_IDS)[keyof typeof BATCH_ENRICHMENT_JOB_IDS];

// =============================================================================
// BUSINESS INTEGRATION CONFIG IDs
// =============================================================================

export const BUSINESS_INTEGRATION_CONFIG_IDS = {
  // OpsMatrix Capital configs
  FUNDING_RESEND: seedId('biz-config-funding-resend', 1),
  FUNDING_RETELL: seedId('biz-config-funding-retell', 1),
  FUNDING_OPENAI: seedId('biz-config-funding-openai', 1),

  // 1Sharpe Capital configs (BYOK)
  SHARPE_RESEND_BYOK: seedId('biz-config-sharpe-resend', 1),
  SHARPE_OPENAI_BYOK: seedId('biz-config-sharpe-openai', 1),

  // Pacific Bridge Fund configs
  PACIFIC_RESEND: seedId('biz-config-pacific-resend', 1),
} as const;

export type BusinessIntegrationConfigId =
  (typeof BUSINESS_INTEGRATION_CONFIG_IDS)[keyof typeof BUSINESS_INTEGRATION_CONFIG_IDS];

// =============================================================================
// DATA TYPES
// =============================================================================

export interface ConnectorSeedData {
  id: string;
  businessId: string;
  type: string;
  name: string;
  status: 'PENDING' | 'CONNECTED' | 'DISCONNECTED' | 'ERROR';
  metadata?: Record<string, unknown>;
}

export interface WebhookSubscriptionSeedData {
  id: string;
  businessId: string;
  connectorId?: string;
  url: string;
  events: string[];
  status: 'ACTIVE' | 'PAUSED' | 'FAILED' | 'DISABLED';
  description?: string;
}

export interface EnrichmentJobSeedData {
  id: string;
  businessId: string;
  connectorId?: string;
  targetType: 'lead' | 'opportunity' | 'property' | 'consumer';
  targetIds: string[];
  enrichmentTypes: string[];
  status: 'PENDING' | 'IN_PROGRESS' | 'COMPLETED' | 'FAILED' | 'PARTIAL';
  totalCount: number;
  completedCount: number;
  failedCount: number;
}

export interface ProspectImportJobSeedData {
  id: string;
  businessId: string;
  connectorId?: string;
  searchCriteria: Record<string, unknown>;
  status: 'PENDING' | 'SEARCHING' | 'IMPORTING' | 'COMPLETED' | 'FAILED' | 'CANCELLED';
  totalFound: number;
  imported: number;
  duplicates: number;
  failed: number;
  assignedTo?: string;
  campaignId?: string;
}

export interface PlatformIntegrationConfigSeedData {
  id: string;
  provider: string;
  isEnabled: boolean;
  allowByok: boolean;
  defaultConfig: Record<string, unknown>;
  usageThisMonth?: number;
  usageLimit?: number;
}

export interface BusinessIntegrationConfigSeedData {
  id: string;
  businessId: string;
  provider: string;
  managedBy: 'platform' | 'business';
  config: Record<string, unknown>;
  isVerified: boolean;
  isEnabled: boolean;
  usageThisMonth?: number;
}

export interface WebhookDeliverySeedData {
  id: string;
  subscriptionId: string;
  eventType: string;
  payload: Record<string, unknown>;
  responseStatus?: number;
  responseBody?: string;
  attempts: number;
  nextRetryAt?: string;
  deliveredAt?: string;
  createdAt: string;
}

export interface ConnectorUsageSeedData {
  id: string;
  businessId: string;
  connectorId?: string;
  connectorType: string;
  operation: string;
  count: number;
  date: string; // YYYY-MM-DD format
  metadata?: Record<string, unknown>;
}

export interface BatchEnrichmentJobSeedData {
  id: string;
  businessId: string;
  connectorId?: string;
  targetType: string;
  targetIds: string[];
  enrichmentTypes: string[];
  status: 'PENDING' | 'IN_PROGRESS' | 'COMPLETED' | 'FAILED' | 'PARTIAL';
  totalCount: number;
  completedCount: number;
  failedCount: number;
  errorMessages: string[];
  startedAt?: string;
  completedAt?: string;
}

// =============================================================================
// CONNECTOR DATA
// =============================================================================

export const CONNECTORS: Record<keyof typeof CONNECTOR_IDS, ConnectorSeedData> = {
  FUNDING_SFR_ANALYTICS: {
    id: CONNECTOR_IDS.FUNDING_SFR_ANALYTICS,
    businessId: BUSINESS_IDS.OPSMATRIX_CAPITAL,
    type: 'sfr-analytics',
    name: 'SFR Analytics Production',
    status: 'CONNECTED',
    metadata: { region: 'us-central', tier: 'enterprise' },
  },
  FUNDING_SALESFORCE: {
    id: CONNECTOR_IDS.FUNDING_SALESFORCE,
    businessId: BUSINESS_IDS.OPSMATRIX_CAPITAL,
    type: 'salesforce',
    name: 'Salesforce CRM',
    status: 'CONNECTED',
    metadata: { instanceUrl: 'https://opsmatrix.my.salesforce.com' },
  },
  FUNDING_SLACK: {
    id: CONNECTOR_IDS.FUNDING_SLACK,
    businessId: BUSINESS_IDS.OPSMATRIX_CAPITAL,
    type: 'slack',
    name: 'Team Slack Workspace',
    status: 'CONNECTED',
    metadata: { workspace: 'opsmatrix-capital' },
  },
  FUNDING_SMARTY: {
    id: CONNECTOR_IDS.FUNDING_SMARTY,
    businessId: BUSINESS_IDS.OPSMATRIX_CAPITAL,
    type: 'smarty-streets',
    name: 'SmartyStreets Address Validation',
    status: 'CONNECTED',
  },
  SHARPE_SFR_ANALYTICS: {
    id: CONNECTOR_IDS.SHARPE_SFR_ANALYTICS,
    businessId: BUSINESS_IDS.ONE_SHARPE_CAPITAL,
    type: 'sfr-analytics',
    name: 'SFR Analytics',
    status: 'CONNECTED',
    metadata: { region: 'us-east', tier: 'professional' },
  },
  SHARPE_HUBSPOT: {
    id: CONNECTOR_IDS.SHARPE_HUBSPOT,
    businessId: BUSINESS_IDS.ONE_SHARPE_CAPITAL,
    type: 'hubspot',
    name: 'HubSpot CRM',
    status: 'CONNECTED',
    metadata: { portalId: '12345678' },
  },
  PACIFIC_SLACK: {
    id: CONNECTOR_IDS.PACIFIC_SLACK,
    businessId: BUSINESS_IDS.PACIFIC_BRIDGE_FUND,
    type: 'slack',
    name: 'Pacific Bridge Slack',
    status: 'DISCONNECTED',
  },
};

// =============================================================================
// WEBHOOK SUBSCRIPTION DATA
// =============================================================================

export const WEBHOOK_SUBSCRIPTIONS: Record<keyof typeof WEBHOOK_SUBSCRIPTION_IDS, WebhookSubscriptionSeedData> = {
  FUNDING_DEAL_UPDATES: {
    id: WEBHOOK_SUBSCRIPTION_IDS.FUNDING_DEAL_UPDATES,
    businessId: BUSINESS_IDS.OPSMATRIX_CAPITAL,
    connectorId: CONNECTOR_IDS.FUNDING_SALESFORCE,
    url: 'https://api.opsmatrix.local/webhooks/salesforce/deals',
    events: ['deal.created', 'deal.updated', 'deal.closed'],
    status: 'ACTIVE',
    description: 'Sync deal updates from Salesforce',
  },
  FUNDING_PAYMENT_EVENTS: {
    id: WEBHOOK_SUBSCRIPTION_IDS.FUNDING_PAYMENT_EVENTS,
    businessId: BUSINESS_IDS.OPSMATRIX_CAPITAL,
    url: 'https://api.opsmatrix.local/webhooks/stripe/payments',
    events: ['payment.succeeded', 'payment.failed', 'invoice.paid'],
    status: 'ACTIVE',
    description: 'Payment event notifications',
  },
  SHARPE_LEAD_EVENTS: {
    id: WEBHOOK_SUBSCRIPTION_IDS.SHARPE_LEAD_EVENTS,
    businessId: BUSINESS_IDS.ONE_SHARPE_CAPITAL,
    connectorId: CONNECTOR_IDS.SHARPE_HUBSPOT,
    url: 'https://api.1sharpe.local/webhooks/hubspot/leads',
    events: ['contact.created', 'contact.updated', 'deal.created'],
    status: 'ACTIVE',
    description: 'HubSpot lead sync',
  },
};

// =============================================================================
// ENRICHMENT JOB DATA
// =============================================================================

export const ENRICHMENT_JOBS: Record<keyof typeof ENRICHMENT_JOB_IDS, EnrichmentJobSeedData> = {
  FUNDING_LEAD_BATCH_1: {
    id: ENRICHMENT_JOB_IDS.FUNDING_LEAD_BATCH_1,
    businessId: BUSINESS_IDS.OPSMATRIX_CAPITAL,
    connectorId: CONNECTOR_IDS.FUNDING_SFR_ANALYTICS,
    targetType: 'lead',
    targetIds: ['lead-1', 'lead-2', 'lead-3', 'lead-4', 'lead-5'],
    enrichmentTypes: ['buyer_profile', 'property_history', 'credit_indicators'],
    status: 'COMPLETED',
    totalCount: 5,
    completedCount: 5,
    failedCount: 0,
  },
  FUNDING_PROPERTY_BATCH_1: {
    id: ENRICHMENT_JOB_IDS.FUNDING_PROPERTY_BATCH_1,
    businessId: BUSINESS_IDS.OPSMATRIX_CAPITAL,
    connectorId: CONNECTOR_IDS.FUNDING_SFR_ANALYTICS,
    targetType: 'property',
    targetIds: ['prop-1', 'prop-2', 'prop-3'],
    enrichmentTypes: ['valuation', 'comparables', 'market_trends'],
    status: 'IN_PROGRESS',
    totalCount: 3,
    completedCount: 2,
    failedCount: 0,
  },
  SHARPE_LEAD_BATCH_1: {
    id: ENRICHMENT_JOB_IDS.SHARPE_LEAD_BATCH_1,
    businessId: BUSINESS_IDS.ONE_SHARPE_CAPITAL,
    connectorId: CONNECTOR_IDS.SHARPE_SFR_ANALYTICS,
    targetType: 'lead',
    targetIds: ['lead-a', 'lead-b'],
    enrichmentTypes: ['buyer_profile'],
    status: 'COMPLETED',
    totalCount: 2,
    completedCount: 2,
    failedCount: 0,
  },
};

// =============================================================================
// PROSPECT IMPORT JOB DATA
// =============================================================================

export const PROSPECT_IMPORT_JOBS: Record<keyof typeof PROSPECT_IMPORT_JOB_IDS, ProspectImportJobSeedData> = {
  FUNDING_AUSTIN_IMPORT: {
    id: PROSPECT_IMPORT_JOB_IDS.FUNDING_AUSTIN_IMPORT,
    businessId: BUSINESS_IDS.OPSMATRIX_CAPITAL,
    connectorId: CONNECTOR_IDS.FUNDING_SFR_ANALYTICS,
    searchCriteria: {
      location: { city: 'Austin', state: 'TX' },
      propertyTypes: ['single_family', 'multi_family'],
      purchaseDateRange: { start: '2024-01-01', end: '2024-12-31' },
      minPurchasePrice: 200000,
      maxPurchasePrice: 1000000,
    },
    status: 'COMPLETED',
    totalFound: 245,
    imported: 198,
    duplicates: 42,
    failed: 5,
  },
  FUNDING_DALLAS_IMPORT: {
    id: PROSPECT_IMPORT_JOB_IDS.FUNDING_DALLAS_IMPORT,
    businessId: BUSINESS_IDS.OPSMATRIX_CAPITAL,
    connectorId: CONNECTOR_IDS.FUNDING_SFR_ANALYTICS,
    searchCriteria: {
      location: { city: 'Dallas', state: 'TX' },
      propertyTypes: ['single_family'],
      purchaseDateRange: { start: '2024-06-01', end: '2024-12-31' },
    },
    status: 'IMPORTING',
    totalFound: 156,
    imported: 89,
    duplicates: 23,
    failed: 0,
  },
};

// =============================================================================
// PLATFORM INTEGRATION CONFIG DATA
// =============================================================================

export const PLATFORM_INTEGRATION_CONFIGS: Record<
  keyof typeof PLATFORM_INTEGRATION_CONFIG_IDS,
  PlatformIntegrationConfigSeedData
> = {
  RESEND_PLATFORM: {
    id: PLATFORM_INTEGRATION_CONFIG_IDS.RESEND_PLATFORM,
    provider: 'resend',
    isEnabled: true,
    allowByok: true,
    defaultConfig: {
      dailyLimit: 1000,
      fromDomain: 'mail.opsmatrix.com',
      replyTo: 'support@opsmatrix.com',
    },
    usageThisMonth: 2450,
    usageLimit: 50000,
  },
  RETELL_PLATFORM: {
    id: PLATFORM_INTEGRATION_CONFIG_IDS.RETELL_PLATFORM,
    provider: 'retell',
    isEnabled: true,
    allowByok: true,
    defaultConfig: {
      maxConcurrentCalls: 10,
      defaultVoice: 'sarah',
      recordCalls: true,
    },
    usageThisMonth: 156,
    usageLimit: 1000,
  },
  OPENAI_PLATFORM: {
    id: PLATFORM_INTEGRATION_CONFIG_IDS.OPENAI_PLATFORM,
    provider: 'openai',
    isEnabled: true,
    allowByok: true,
    defaultConfig: {
      defaultModel: 'gpt-4-turbo',
      maxTokens: 4096,
    },
    usageThisMonth: 125000,
    usageLimit: 1000000,
  },
  ANTHROPIC_PLATFORM: {
    id: PLATFORM_INTEGRATION_CONFIG_IDS.ANTHROPIC_PLATFORM,
    provider: 'anthropic',
    isEnabled: true,
    allowByok: true,
    defaultConfig: {
      defaultModel: 'claude-3-5-sonnet',
      maxTokens: 8192,
    },
    usageThisMonth: 450000,
    usageLimit: 2000000,
  },
  SFR_ANALYTICS_PLATFORM: {
    id: PLATFORM_INTEGRATION_CONFIG_IDS.SFR_ANALYTICS_PLATFORM,
    provider: 'sfr-analytics',
    isEnabled: true,
    allowByok: true,
    defaultConfig: {
      apiVersion: 'v2',
      rateLimit: 100,
    },
    usageThisMonth: 8500,
    usageLimit: 50000,
  },
  // Issue #300: DocuSeal e-signature service (self-hosted)
  DOCUSEAL_PLATFORM: {
    id: PLATFORM_INTEGRATION_CONFIG_IDS.DOCUSEAL_PLATFORM,
    provider: 'docuseal',
    isEnabled: true,
    allowByok: false, // Self-hosted, platform provides instance
    defaultConfig: {
      // Docker: use DOCUSEAL_API_URL env var (set by docker-compose).
      // Local dev: localhost:3005 (DocuSeal Docker port mapping).
      // Note: process.env is NOT available in browser — this seed data is used server-side only
      // during db:seed. The seeder resolves the env var at seed time.
      instanceUrl: 'http://localhost:3005',
      defaultExpirationDays: 30,
      sendEmailNotifications: true,
      downloadFormat: 'pdf',
    },
    usageThisMonth: 45,
    usageLimit: 500, // Submissions per month
  },
};

// =============================================================================
// WEBHOOK DELIVERY DATA
// =============================================================================

export const WEBHOOK_DELIVERIES: Record<keyof typeof WEBHOOK_DELIVERY_IDS, WebhookDeliverySeedData> = {
  FUNDING_DEAL_DELIVERY_1: {
    id: WEBHOOK_DELIVERY_IDS.FUNDING_DEAL_DELIVERY_1,
    subscriptionId: WEBHOOK_SUBSCRIPTION_IDS.FUNDING_DEAL_UPDATES,
    eventType: 'deal.created',
    payload: {
      dealId: 'sf-deal-001',
      name: 'New Bridge Loan Application',
      amount: 450000,
      stage: 'Qualification',
    },
    responseStatus: 200,
    responseBody: '{"success":true}',
    attempts: 1,
    deliveredAt: '2026-01-21T10:15:00Z',
    createdAt: '2026-01-21T10:15:00Z',
  },
  FUNDING_DEAL_DELIVERY_2: {
    id: WEBHOOK_DELIVERY_IDS.FUNDING_DEAL_DELIVERY_2,
    subscriptionId: WEBHOOK_SUBSCRIPTION_IDS.FUNDING_DEAL_UPDATES,
    eventType: 'deal.updated',
    payload: {
      dealId: 'sf-deal-001',
      changes: { stage: { from: 'Qualification', to: 'Underwriting' } },
    },
    responseStatus: 200,
    responseBody: '{"success":true}',
    attempts: 1,
    deliveredAt: '2026-01-21T14:30:00Z',
    createdAt: '2026-01-21T14:30:00Z',
  },
  FUNDING_PAYMENT_DELIVERY_1: {
    id: WEBHOOK_DELIVERY_IDS.FUNDING_PAYMENT_DELIVERY_1,
    subscriptionId: WEBHOOK_SUBSCRIPTION_IDS.FUNDING_PAYMENT_EVENTS,
    eventType: 'payment.succeeded',
    payload: {
      paymentId: 'pi_abc123',
      amount: 2500000,
      currency: 'usd',
      description: 'Loan origination fee',
    },
    responseStatus: 200,
    responseBody: '{"received":true}',
    attempts: 1,
    deliveredAt: '2026-01-20T16:45:00Z',
    createdAt: '2026-01-20T16:45:00Z',
  },
  FUNDING_PAYMENT_DELIVERY_FAILED: {
    id: WEBHOOK_DELIVERY_IDS.FUNDING_PAYMENT_DELIVERY_FAILED,
    subscriptionId: WEBHOOK_SUBSCRIPTION_IDS.FUNDING_PAYMENT_EVENTS,
    eventType: 'payment.failed',
    payload: {
      paymentId: 'pi_xyz789',
      amount: 175000,
      error: 'insufficient_funds',
    },
    responseStatus: 503,
    responseBody: 'Service temporarily unavailable',
    attempts: 3,
    nextRetryAt: '2026-01-21T18:00:00Z',
    createdAt: '2026-01-21T15:00:00Z',
  },
  SHARPE_LEAD_DELIVERY_1: {
    id: WEBHOOK_DELIVERY_IDS.SHARPE_LEAD_DELIVERY_1,
    subscriptionId: WEBHOOK_SUBSCRIPTION_IDS.SHARPE_LEAD_EVENTS,
    eventType: 'contact.created',
    payload: {
      contactId: 'hs-contact-001',
      email: 'developer@constructionco.com',
      firstName: 'Mike',
      lastName: 'Builder',
      source: 'Website',
    },
    responseStatus: 200,
    responseBody: '{"imported":true,"leadId":"lead-sharpe-001"}',
    attempts: 1,
    deliveredAt: '2026-01-21T09:00:00Z',
    createdAt: '2026-01-21T09:00:00Z',
  },
};

// =============================================================================
// CONNECTOR USAGE DATA
// =============================================================================

export const CONNECTOR_USAGES: Record<keyof typeof CONNECTOR_USAGE_IDS, ConnectorUsageSeedData> = {
  FUNDING_SFR_SEARCH_JAN21: {
    id: CONNECTOR_USAGE_IDS.FUNDING_SFR_SEARCH_JAN21,
    businessId: BUSINESS_IDS.OPSMATRIX_CAPITAL,
    connectorId: CONNECTOR_IDS.FUNDING_SFR_ANALYTICS,
    connectorType: 'sfr-analytics',
    operation: 'search_buyer',
    count: 156,
    date: '2026-01-21',
    metadata: { avgResponseTime: 245, successRate: 0.98 },
  },
  FUNDING_SFR_ENRICH_JAN21: {
    id: CONNECTOR_USAGE_IDS.FUNDING_SFR_ENRICH_JAN21,
    businessId: BUSINESS_IDS.OPSMATRIX_CAPITAL,
    connectorId: CONNECTOR_IDS.FUNDING_SFR_ANALYTICS,
    connectorType: 'sfr-analytics',
    operation: 'batch_enrich',
    count: 42,
    date: '2026-01-21',
    metadata: { batchSize: 50, totalRecords: 2100 },
  },
  FUNDING_SALESFORCE_SYNC_JAN21: {
    id: CONNECTOR_USAGE_IDS.FUNDING_SALESFORCE_SYNC_JAN21,
    businessId: BUSINESS_IDS.OPSMATRIX_CAPITAL,
    connectorId: CONNECTOR_IDS.FUNDING_SALESFORCE,
    connectorType: 'salesforce',
    operation: 'sync_deals',
    count: 89,
    date: '2026-01-21',
    metadata: { created: 12, updated: 77 },
  },
  FUNDING_SMARTY_VALIDATE_JAN21: {
    id: CONNECTOR_USAGE_IDS.FUNDING_SMARTY_VALIDATE_JAN21,
    businessId: BUSINESS_IDS.OPSMATRIX_CAPITAL,
    connectorId: CONNECTOR_IDS.FUNDING_SMARTY,
    connectorType: 'smarty-streets',
    operation: 'validate_address',
    count: 312,
    date: '2026-01-21',
    metadata: { valid: 298, invalid: 14 },
  },
  SHARPE_SFR_SEARCH_JAN21: {
    id: CONNECTOR_USAGE_IDS.SHARPE_SFR_SEARCH_JAN21,
    businessId: BUSINESS_IDS.ONE_SHARPE_CAPITAL,
    connectorId: CONNECTOR_IDS.SHARPE_SFR_ANALYTICS,
    connectorType: 'sfr-analytics',
    operation: 'search_buyer',
    count: 67,
    date: '2026-01-21',
    metadata: { avgResponseTime: 312, successRate: 0.97 },
  },
  SHARPE_HUBSPOT_SYNC_JAN21: {
    id: CONNECTOR_USAGE_IDS.SHARPE_HUBSPOT_SYNC_JAN21,
    businessId: BUSINESS_IDS.ONE_SHARPE_CAPITAL,
    connectorId: CONNECTOR_IDS.SHARPE_HUBSPOT,
    connectorType: 'hubspot',
    operation: 'sync_contacts',
    count: 45,
    date: '2026-01-21',
    metadata: { created: 8, updated: 37 },
  },
};

// =============================================================================
// BATCH ENRICHMENT JOB DATA
// =============================================================================

export const BATCH_ENRICHMENT_JOBS: Record<keyof typeof BATCH_ENRICHMENT_JOB_IDS, BatchEnrichmentJobSeedData> = {
  FUNDING_LEAD_BATCH_WEEKLY: {
    id: BATCH_ENRICHMENT_JOB_IDS.FUNDING_LEAD_BATCH_WEEKLY,
    businessId: BUSINESS_IDS.OPSMATRIX_CAPITAL,
    connectorId: CONNECTOR_IDS.FUNDING_SFR_ANALYTICS,
    targetType: 'lead',
    targetIds: Array.from({ length: 50 }, (_, i) => `lead-batch-${i + 1}`),
    enrichmentTypes: ['buyer_profile', 'property_history', 'credit_indicators'],
    status: 'COMPLETED',
    totalCount: 50,
    completedCount: 48,
    failedCount: 2,
    errorMessages: ['Lead lead-batch-23: No matching buyer profile found', 'Lead lead-batch-41: API timeout'],
    startedAt: '2026-01-20T02:00:00Z',
    completedAt: '2026-01-20T04:35:00Z',
  },
  FUNDING_PROPERTY_BATCH_DAILY: {
    id: BATCH_ENRICHMENT_JOB_IDS.FUNDING_PROPERTY_BATCH_DAILY,
    businessId: BUSINESS_IDS.OPSMATRIX_CAPITAL,
    connectorId: CONNECTOR_IDS.FUNDING_SFR_ANALYTICS,
    targetType: 'property',
    targetIds: Array.from({ length: 25 }, (_, i) => `prop-daily-${i + 1}`),
    enrichmentTypes: ['valuation', 'comparables', 'market_trends'],
    status: 'IN_PROGRESS',
    totalCount: 25,
    completedCount: 18,
    failedCount: 0,
    errorMessages: [],
    startedAt: '2026-01-21T06:00:00Z',
  },
  SHARPE_CONSTRUCTION_BATCH: {
    id: BATCH_ENRICHMENT_JOB_IDS.SHARPE_CONSTRUCTION_BATCH,
    businessId: BUSINESS_IDS.ONE_SHARPE_CAPITAL,
    connectorId: CONNECTOR_IDS.SHARPE_SFR_ANALYTICS,
    targetType: 'property',
    targetIds: ['const-prop-1', 'const-prop-2', 'const-prop-3', 'const-prop-4', 'const-prop-5'],
    enrichmentTypes: ['valuation', 'permit_history', 'contractor_liens'],
    status: 'COMPLETED',
    totalCount: 5,
    completedCount: 5,
    failedCount: 0,
    errorMessages: [],
    startedAt: '2026-01-21T08:00:00Z',
    completedAt: '2026-01-21T08:15:00Z',
  },
};

// =============================================================================
// BUSINESS INTEGRATION CONFIG DATA
// =============================================================================

export const BUSINESS_INTEGRATION_CONFIGS: Record<
  keyof typeof BUSINESS_INTEGRATION_CONFIG_IDS,
  BusinessIntegrationConfigSeedData
> = {
  // OpsMatrix Capital - uses platform defaults
  FUNDING_RESEND: {
    id: BUSINESS_INTEGRATION_CONFIG_IDS.FUNDING_RESEND,
    businessId: BUSINESS_IDS.OPSMATRIX_CAPITAL,
    provider: 'resend',
    managedBy: 'platform',
    config: {
      fromEmail: 'loans@opsmatrix.capital',
      replyTo: 'support@opsmatrix.capital',
    },
    isVerified: true,
    isEnabled: true,
    usageThisMonth: 1250,
  },
  FUNDING_RETELL: {
    id: BUSINESS_INTEGRATION_CONFIG_IDS.FUNDING_RETELL,
    businessId: BUSINESS_IDS.OPSMATRIX_CAPITAL,
    provider: 'retell',
    managedBy: 'platform',
    config: {
      agentId: 'agent-funding-sales',
      voiceId: 'sarah',
      maxConcurrentCalls: 5,
    },
    isVerified: true,
    isEnabled: true,
    usageThisMonth: 89,
  },
  FUNDING_OPENAI: {
    id: BUSINESS_INTEGRATION_CONFIG_IDS.FUNDING_OPENAI,
    businessId: BUSINESS_IDS.OPSMATRIX_CAPITAL,
    provider: 'openai',
    managedBy: 'platform',
    config: {
      preferredModel: 'gpt-4-turbo',
      maxTokensPerRequest: 4096,
    },
    isVerified: true,
    isEnabled: true,
    usageThisMonth: 45000,
  },

  // 1Sharpe Capital - BYOK (Bring Your Own Key)
  SHARPE_RESEND_BYOK: {
    id: BUSINESS_INTEGRATION_CONFIG_IDS.SHARPE_RESEND_BYOK,
    businessId: BUSINESS_IDS.ONE_SHARPE_CAPITAL,
    provider: 'resend',
    managedBy: 'business',
    config: {
      fromEmail: 'lending@1sharpe.capital',
      fromDomain: 'mail.1sharpe.capital',
      replyTo: 'team@1sharpe.capital',
    },
    isVerified: true,
    isEnabled: true,
    usageThisMonth: 567,
  },
  SHARPE_OPENAI_BYOK: {
    id: BUSINESS_INTEGRATION_CONFIG_IDS.SHARPE_OPENAI_BYOK,
    businessId: BUSINESS_IDS.ONE_SHARPE_CAPITAL,
    provider: 'openai',
    managedBy: 'business',
    config: {
      preferredModel: 'gpt-4o',
      maxTokensPerRequest: 8192,
      customSystemPrompt: 'You are a construction lending specialist...',
    },
    isVerified: true,
    isEnabled: true,
    usageThisMonth: 28000,
  },

  // Pacific Bridge Fund - uses platform defaults
  PACIFIC_RESEND: {
    id: BUSINESS_INTEGRATION_CONFIG_IDS.PACIFIC_RESEND,
    businessId: BUSINESS_IDS.PACIFIC_BRIDGE_FUND,
    provider: 'resend',
    managedBy: 'platform',
    config: {
      fromEmail: 'investments@pacificbridge.fund',
      replyTo: 'investor-relations@pacificbridge.fund',
    },
    isVerified: true,
    isEnabled: true,
    usageThisMonth: 234,
  },
};

// =============================================================================
// ENRICHMENT PORTAL SEED DATA (for business portal enrichment pages)
// =============================================================================

export const ENRICHMENT_PORTAL_JOB_IDS = {
  LEAD_ENRICHMENT_BATCH_1: seedId('enrich-portal-batch1', 1),
  LEAD_ENRICHMENT_BATCH_2: seedId('enrich-portal-batch2', 2),
  CONTACT_ENRICHMENT: seedId('enrich-portal-contact', 3),
  PROPERTY_ENRICHMENT: seedId('enrich-portal-property', 4),
} as const;

export type EnrichmentPortalJobId = (typeof ENRICHMENT_PORTAL_JOB_IDS)[keyof typeof ENRICHMENT_PORTAL_JOB_IDS];

export interface EnrichmentPortalJobSeedData {
  id: string;
  businessId: string;
  type: 'lead' | 'contact' | 'property';
  provider: string;
  status: 'completed' | 'in_progress' | 'failed' | 'queued';
  totalRecords: number;
  enrichedRecords: number;
  failedRecords: number;
  createdBy: string;
  completedAt?: string;
}

export const ENRICHMENT_PORTAL_JOBS: Record<keyof typeof ENRICHMENT_PORTAL_JOB_IDS, EnrichmentPortalJobSeedData> = {
  LEAD_ENRICHMENT_BATCH_1: {
    id: ENRICHMENT_PORTAL_JOB_IDS.LEAD_ENRICHMENT_BATCH_1,
    businessId: BUSINESS_IDS.OPSMATRIX_CAPITAL,
    type: 'lead',
    provider: 'sfr_analytics',
    status: 'completed',
    totalRecords: 150,
    enrichedRecords: 142,
    failedRecords: 8,
    createdBy: USER_IDS.RACHEL_MARTINEZ,
    completedAt: '2026-01-15T14:30:00Z',
  },
  LEAD_ENRICHMENT_BATCH_2: {
    id: ENRICHMENT_PORTAL_JOB_IDS.LEAD_ENRICHMENT_BATCH_2,
    businessId: BUSINESS_IDS.OPSMATRIX_CAPITAL,
    type: 'lead',
    provider: 'sfr_analytics',
    status: 'completed',
    totalRecords: 75,
    enrichedRecords: 71,
    failedRecords: 4,
    createdBy: USER_IDS.RACHEL_MARTINEZ,
    completedAt: '2026-02-01T10:15:00Z',
  },
  CONTACT_ENRICHMENT: {
    id: ENRICHMENT_PORTAL_JOB_IDS.CONTACT_ENRICHMENT,
    businessId: BUSINESS_IDS.OPSMATRIX_CAPITAL,
    type: 'contact',
    provider: 'clearbit',
    status: 'completed',
    totalRecords: 50,
    enrichedRecords: 47,
    failedRecords: 3,
    createdBy: USER_IDS.RACHEL_MARTINEZ,
    completedAt: '2026-02-05T16:45:00Z',
  },
  PROPERTY_ENRICHMENT: {
    id: ENRICHMENT_PORTAL_JOB_IDS.PROPERTY_ENRICHMENT,
    businessId: BUSINESS_IDS.OPSMATRIX_CAPITAL,
    type: 'property',
    provider: 'sfr_analytics',
    status: 'in_progress',
    totalRecords: 200,
    enrichedRecords: 120,
    failedRecords: 0,
    createdBy: USER_IDS.RACHEL_MARTINEZ,
  },
};

// =============================================================================
// HELPER FUNCTIONS
// =============================================================================

export function getAllConnectors(workspace?: string): ConnectorSeedData[] {
  const connectors = Object.values(CONNECTORS);
  if (!workspace) return connectors;
  return connectors.map((c) => ({ ...c, name: `${c.name} [${workspace}]` }));
}

export function getAllWebhookSubscriptions(): WebhookSubscriptionSeedData[] {
  return Object.values(WEBHOOK_SUBSCRIPTIONS);
}

export function getAllEnrichmentJobs(): EnrichmentJobSeedData[] {
  return Object.values(ENRICHMENT_JOBS);
}

export function getAllProspectImportJobs(): ProspectImportJobSeedData[] {
  return Object.values(PROSPECT_IMPORT_JOBS);
}

export function getAllPlatformIntegrationConfigs(): PlatformIntegrationConfigSeedData[] {
  return Object.values(PLATFORM_INTEGRATION_CONFIGS);
}

export function getConnectorsByBusiness(businessId: string): ConnectorSeedData[] {
  return Object.values(CONNECTORS).filter((c) => c.businessId === businessId);
}

export function getConnectorsByType(type: string): ConnectorSeedData[] {
  return Object.values(CONNECTORS).filter((c) => c.type === type);
}

export function getWebhooksByBusiness(businessId: string): WebhookSubscriptionSeedData[] {
  return Object.values(WEBHOOK_SUBSCRIPTIONS).filter((w) => w.businessId === businessId);
}

export function getAllWebhookDeliveries(): WebhookDeliverySeedData[] {
  return Object.values(WEBHOOK_DELIVERIES);
}

export function getWebhookDeliveriesBySubscription(subscriptionId: string): WebhookDeliverySeedData[] {
  return Object.values(WEBHOOK_DELIVERIES).filter((d) => d.subscriptionId === subscriptionId);
}

export function getFailedWebhookDeliveries(): WebhookDeliverySeedData[] {
  return Object.values(WEBHOOK_DELIVERIES).filter((d) => d.nextRetryAt !== undefined);
}

export function getAllConnectorUsages(): ConnectorUsageSeedData[] {
  return Object.values(CONNECTOR_USAGES);
}

export function getConnectorUsagesByBusiness(businessId: string): ConnectorUsageSeedData[] {
  return Object.values(CONNECTOR_USAGES).filter((u) => u.businessId === businessId);
}

export function getConnectorUsagesByType(connectorType: string): ConnectorUsageSeedData[] {
  return Object.values(CONNECTOR_USAGES).filter((u) => u.connectorType === connectorType);
}

export function getAllBatchEnrichmentJobs(): BatchEnrichmentJobSeedData[] {
  return Object.values(BATCH_ENRICHMENT_JOBS);
}

export function getBatchEnrichmentJobsByBusiness(businessId: string): BatchEnrichmentJobSeedData[] {
  return Object.values(BATCH_ENRICHMENT_JOBS).filter((j) => j.businessId === businessId);
}

export function getBatchEnrichmentJobsByStatus(
  status: BatchEnrichmentJobSeedData['status'],
): BatchEnrichmentJobSeedData[] {
  return Object.values(BATCH_ENRICHMENT_JOBS).filter((j) => j.status === status);
}

export function getAllBusinessIntegrationConfigs(): BusinessIntegrationConfigSeedData[] {
  return Object.values(BUSINESS_INTEGRATION_CONFIGS);
}

export function getBusinessIntegrationConfigsByBusiness(businessId: string): BusinessIntegrationConfigSeedData[] {
  return Object.values(BUSINESS_INTEGRATION_CONFIGS).filter((c) => c.businessId === businessId);
}

export function getBusinessIntegrationConfigsByProvider(provider: string): BusinessIntegrationConfigSeedData[] {
  return Object.values(BUSINESS_INTEGRATION_CONFIGS).filter((c) => c.provider === provider);
}

export function getByokConfigs(): BusinessIntegrationConfigSeedData[] {
  return Object.values(BUSINESS_INTEGRATION_CONFIGS).filter((c) => c.managedBy === 'business');
}

// Enrichment Portal
export function getAllEnrichmentPortalJobs(): EnrichmentPortalJobSeedData[] {
  return Object.values(ENRICHMENT_PORTAL_JOBS);
}

export function getEnrichmentPortalJobsByBusiness(businessId: string): EnrichmentPortalJobSeedData[] {
  return Object.values(ENRICHMENT_PORTAL_JOBS).filter((j) => j.businessId === businessId);
}

export function getEnrichmentPortalJobsByStatus(
  status: EnrichmentPortalJobSeedData['status'],
): EnrichmentPortalJobSeedData[] {
  return Object.values(ENRICHMENT_PORTAL_JOBS).filter((j) => j.status === status);
}
